import { EntityId } from '@reduxjs/toolkit';
import { camelCase, keyBy, reverse, sortBy } from 'lodash';
import {
  InferredProgressValue,
  PastProjectStatus,
  ProgressStatus,
  Project,
  ProjectFinancialsContractType,
} from 'api/project';
import { CustomRevenueRecognitionTypes } from 'api/settings-financials/types';
import { StatusGroup } from 'api/tasks';
import { minsToHours } from 'components/organisms/financials/utils/common';
import { FilterableField } from 'components/organisms/tasks/fields';
import { byCreatedAt } from 'utils/comparators/by-created-at';
import { byStartDate } from 'utils/comparators/by-start-date';

export type FieldLookupById = Record<EntityId, FilterableField>;

export const getFieldMapReducer =
  (fieldLookup: FieldLookupById) =>
  (
    hash: Record<string, unknown>,
    {
      fieldValue,
      fieldId,
      metaFieldValue,
    }: {
      fieldId: number;
      fieldValue: unknown;
      metaFieldValue?: unknown;
      isEditable?: boolean;
    },
  ) => {
    const field = fieldLookup[fieldId];

    if (field) {
      hash[field.fieldName] = fieldValue;

      if (metaFieldValue !== undefined) {
        hash[`${field.fieldName}__rl_meta`] = metaFieldValue;
      }
    }

    return hash;
  };
export const getFieldMapReducerV2 =
  () =>
  (
    hash: Record<string, unknown>,
    {
      fieldValue,
      fieldName,
      metaFieldValue,
    }: {
      fieldValue: unknown;
      metaFieldValue?: unknown;
      isEditable?: boolean;
      fieldName: string;
    },
  ) => {
    if (fieldName) {
      hash[fieldName] = fieldValue;
      // in some places, the lowercase field name is used, so we need to add it to the hash
      hash[fieldName?.toLowerCase()] = fieldValue;
      hash[camelCase(fieldName)] = fieldValue;

      if (metaFieldValue !== undefined) {
        hash[`${fieldName}__rl_meta`] = metaFieldValue;
        hash[`${camelCase(fieldName)}__rl_meta`] = metaFieldValue;
        // in some places, the lowercase field name is used, so we need to add it to the hash
        hash[`${fieldName?.toLowerCase()}__rl_meta`] = metaFieldValue;
        hash[`${camelCase(fieldName)?.toLowerCase()}__rl_meta`] =
          metaFieldValue;
      }
    }

    return hash;
  };

export const calculateProgressPercent = (
  progressStatus?: ProgressStatus[],
  totalTaskCount = 0,
) => {
  const progressLookup = keyBy(progressStatus ?? [], 'group');
  const completed = progressLookup[StatusGroup.Completed]?.count ?? 0;
  const total = totalTaskCount ?? 0;
  let progress = completed / total;

  progress = Number.isNaN(progress) ? 0 : progress;

  return Math.round(progress * 100);
};

export const lastStatusUpdatedAt = (statuses: PastProjectStatus[]) =>
  reverse(sortBy(statuses, 'updatedAt'))[0]?.updatedAt;

export type ProjectListItem = Omit<
  Project,
  | 'fields'
  | 'projectOwner'
  | 'currentPhases'
  | 'customer'
  | 'partners'
  | 'teamMembers'
  | 'inferredProgress'
> &
  Record<string, unknown> & {
    progressPercent: number;
    meta: Pick<Project, 'customer'>;
  } & { rowId?: string };

type TransformFunction = (
  fieldLookup: FieldLookupById,
  isPaginationEnabled?: boolean,
  groupKey?: string | null,
) => (data: Project[]) => ProjectListItem[];

type TransformFunctionV2 = (
  isPaginationEnabled?: boolean,
  groupKey?: string | null,
) => (data: Project[]) => ProjectListItem[];

export const transform: TransformFunction =
  (fieldLookup, isPaginationEnabled = false, groupKey = null) =>
  (data) => {
    const initial: ProjectListItem[] = [];
    const toFieldMap = getFieldMapReducer(fieldLookup);

    let projectData: Project[] = data;

    if (isPaginationEnabled) {
      projectData = data;
    } else {
      projectData = data.sort(byCreatedAt).reverse();
    }

    return projectData.reduce(
      (
        list,
        { fields, customer, teamMembers, inferredProgress, partners, ...rest },
      ) => {
        const fieldsMap = (fields ?? []).reduce(toFieldMap, {});
        const { currentPhases = [] } = rest;
        const currentPhasesValue = currentPhases
          .sort(byStartDate)
          .reverse()
          .map(({ projectPhaseName }) => projectPhaseName);

        const progressPercent = calculateProgressPercent(
          rest.progressStatus,
          rest.totalTaskCount,
        );

        const meta = {
          customer,
        };

        list.push({
          ...rest,
          ...fieldsMap,
          rowId:
            groupKey != null
              ? `${groupKey}_${rest.projectId}`
              : rest.projectId.toString(),
          currentPhases:
            currentPhasesValue.length > 0 ? currentPhasesValue : null,
          progressPercent,
          meta,
          arr: rest.annualizedRecurringRevenue,
          customer: customer.companyId,
          partnerCompany: partners?.map(({ companyId }) => companyId),
          projectOwner: rest.projectOwner?.userId,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          projectOwner__rl_meta: rest.projectOwner,
          inferredProgress:
            inferredProgress?.value === InferredProgressValue.None
              ? undefined
              : inferredProgress?.value,
          estimatedDueDate: rest.inferredProgressDays,
          teamMembers: (teamMembers ?? []).map(({ userId }) => userId),
          projectMinutes: {
            budget_hours:
              rest.financialContractType ===
              ProjectFinancialsContractType.SUBSCRIPTION
                ? minsToHours(
                    (rest.projectFinancialsDetailModel
                      ?.projectFinancialsMetaData?.periodMinutes ?? 0) *
                      (rest.currentPeriodData?.numberOfPeriods ?? 1),
                  )
                : rest.budgetedHours,
            budgetMinutes:
              rest.financialContractType ===
              ProjectFinancialsContractType.SUBSCRIPTION
                ? (rest.projectFinancialsDetailModel?.projectFinancialsMetaData
                    ?.periodMinutes ?? 0) *
                  (rest.projectFinancialsDetailModel?.projectFinancialsMetaData
                    ?.numberOfPeriods ?? 1)
                : rest.budgetedMinutes,
            total_minutes: rest.totalMinutes,
            project_name: rest.projectName,
            billableMinutes: (rest?.metrics?.billableHours ?? 0) * 60,
          },
          billableMinutes: (rest?.metrics?.billableHours ?? 0) * 60,
          lastStatusUpdate: lastStatusUpdatedAt(rest.pastProjectStatus ?? []),
          totalAllocatedHours: rest?.metrics?.totalAllocatedHours ?? 0,
          estimateAtCompletion: (rest?.metrics?.estimateAtCompletion ?? 0) * 60,
          estimateToComplete: (rest?.metrics?.estimateToComplete ?? 0) * 60,
          currency: rest?.projectFinancialsDetailModel?.currency,
          projectBudget: rest?.projectFinancialsDetailModel?.projectBudget,
          actualRevenue: rest?.metrics?.actualRevenue,
          totalRecognisedRevenue: rest?.metrics?.totalRecognisedRevenue,
          actualCost: rest?.metrics?.actualCost,
          actualProfit: rest?.metrics?.actualProfit,
          actualProfitMargin: rest?.metrics?.actualProfitMargin,
          currentPeriodData: rest?.currentPeriodData,
          subscriptionFrequency:
            rest?.projectFinancialsDetailModel?.projectFinancialsMetaData
              ?.subscriptionFrequency,
          revenueRecognitionType:
            rest?.projectFinancialsDetailModel?.revenueRecognitionType ===
              CustomRevenueRecognitionTypes.CustomRevenueRecognition &&
            rest?.projectFinancialsDetailModel?.customRevenueRecognitionConfigId
              ? `${CustomRevenueRecognitionTypes.CustomRevenueRecognition}_${rest?.projectFinancialsDetailModel?.customRevenueRecognitionConfigId}`
              : rest?.projectFinancialsDetailModel?.revenueRecognitionType,
          financialContractType:
            rest?.projectFinancialsDetailModel?.financialContractType,
          currentPeriodNumber: rest?.currentPeriodData?.currentPeriodNumber,
          periodBudget:
            rest?.projectFinancialsDetailModel?.projectFinancialsMetaData
              ?.periodBudget,
          periodMinutes:
            rest?.projectFinancialsDetailModel?.projectFinancialsMetaData
              ?.periodMinutes,
          periodRevenue: rest?.currentPeriodData?.periodRevenue,
          periodTotalTimeTracked:
            rest?.currentPeriodData?.periodTotalTimeTracked,
          currentPeriodStartDate:
            rest?.currentPeriodData?.currentPeriodStartDate,
          currentPeriodEndDate: rest?.currentPeriodData?.currentPeriodEndDate,
        });

        return list;
      },
      initial,
    );
  };

export const transformV2: TransformFunctionV2 =
  (isPaginationEnabled = false, groupKey = null) =>
  (data) => {
    const initial: ProjectListItem[] = [];
    const toFieldMap = getFieldMapReducerV2();

    let projectData: Project[] = data;

    if (isPaginationEnabled) {
      projectData = data;
    } else {
      projectData = data.sort(byCreatedAt).reverse();
    }

    return projectData.reduce(
      (
        list,
        { fields, customer, teamMembers, inferredProgress, partners, ...rest },
      ) => {
        const fieldsMap = (fields ?? []).reduce(toFieldMap, {});
        const { currentPhases = [] } = rest;
        const currentPhasesValue = currentPhases
          .sort(byStartDate)
          .reverse()
          .map(({ projectPhaseName }) => projectPhaseName);

        const progressPercent = calculateProgressPercent(
          rest.progressStatus,
          rest.totalTaskCount,
        );

        const meta = {
          customer,
        };

        list.push({
          ...rest,
          ...fieldsMap,
          rowId:
            groupKey != null
              ? `${groupKey}_${rest.projectId}`
              : rest.projectId.toString(),
          currentPhases:
            currentPhasesValue.length > 0 ? currentPhasesValue : null,
          progressPercent,
          meta,
          arr: rest.annualizedRecurringRevenue,
          customer: customer.companyId,
          partnerCompany: partners?.map(({ companyId }) => companyId),
          projectOwner: rest.projectOwner?.userId,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          projectOwner__rl_meta: rest.projectOwner,
          inferredProgress:
            inferredProgress?.value === InferredProgressValue.None
              ? undefined
              : inferredProgress?.value,
          estimatedDueDate: rest.inferredProgressDays,
          teamMembers,
          projectMinutes: {
            budget_hours:
              rest.financialContractType ===
              ProjectFinancialsContractType.SUBSCRIPTION
                ? minsToHours(
                    (rest.projectFinancialsDetailModel
                      ?.projectFinancialsMetaData?.periodMinutes ?? 0) *
                      (rest.currentPeriodData?.numberOfPeriods ?? 1),
                  )
                : rest.budgetedHours,
            budgetMinutes:
              rest.financialContractType ===
              ProjectFinancialsContractType.SUBSCRIPTION
                ? (rest.projectFinancialsDetailModel?.projectFinancialsMetaData
                    ?.periodMinutes ?? 0) *
                  (rest.projectFinancialsDetailModel?.projectFinancialsMetaData
                    ?.numberOfPeriods ?? 1)
                : rest.budgetedMinutes,
            total_minutes: rest.totalMinutes,
            project_name: rest.projectName,
            billableMinutes: (rest?.metrics?.billableHours ?? 0) * 60,
          },
          billableMinutes: (rest?.metrics?.billableHours ?? 0) * 60,
          lastStatusUpdate: lastStatusUpdatedAt(rest.pastProjectStatus ?? []),
          totalAllocatedHours: rest?.metrics?.totalAllocatedHours ?? 0,
          estimateAtCompletion: (rest?.metrics?.estimateAtCompletion ?? 0) * 60,
          estimateToComplete: (rest?.metrics?.estimateToComplete ?? 0) * 60,
          currency: rest?.projectFinancialsDetailModel?.currency,
          projectBudget: rest?.projectFinancialsDetailModel?.projectBudget,
          actualRevenue: rest?.metrics?.actualRevenue,
          actualCost: rest?.metrics?.actualCost,
          actualProfit: rest?.metrics?.actualProfit,
          actualProfitMargin: rest?.metrics?.actualProfitMargin,
          currentPeriodData: rest?.currentPeriodData,
          subscriptionFrequency:
            rest?.projectFinancialsDetailModel?.projectFinancialsMetaData
              ?.subscriptionFrequency,
          revenueRecognitionType:
            rest?.projectFinancialsDetailModel?.revenueRecognitionType ===
              CustomRevenueRecognitionTypes.CustomRevenueRecognition &&
            rest?.projectFinancialsDetailModel?.customRevenueRecognitionConfigId
              ? `${CustomRevenueRecognitionTypes.CustomRevenueRecognition}_${rest?.projectFinancialsDetailModel?.customRevenueRecognitionConfigId}`
              : rest?.projectFinancialsDetailModel?.revenueRecognitionType,
          financialContractType:
            rest?.projectFinancialsDetailModel?.financialContractType,
          currentPeriodNumber: rest?.currentPeriodData?.currentPeriodNumber,
          periodBudget:
            rest?.projectFinancialsDetailModel?.projectFinancialsMetaData
              ?.periodBudget,
          periodMinutes:
            rest?.projectFinancialsDetailModel?.projectFinancialsMetaData
              ?.periodMinutes,
          periodRevenue: rest?.currentPeriodData?.periodRevenue,
          periodTotalTimeTracked:
            rest?.currentPeriodData?.periodTotalTimeTracked,
          currentPeriodStartDate:
            rest?.currentPeriodData?.currentPeriodStartDate,
          currentPeriodEndDate: rest?.currentPeriodData?.currentPeriodEndDate,
        });

        return list;
      },
      initial,
    );
  };
export const FAILED_ROW_TYPE = 'failed-to-load';
export const isFailedRow = (row?: ProjectListItem) =>
  row?.gridRowType === FAILED_ROW_TYPE;

export const generateFailedRows = ({
  count,
  startRow,
  groupKey,
}: {
  count: number;
  startRow: number;
  groupKey?: string | null;
}): ProjectListItem[] => {
  const failedRow = (id: number) =>
    ({
      projectId: id,
      rowId: groupKey != null ? `${groupKey}_${id}` : id.toString(),
      gridRowType: FAILED_ROW_TYPE,
    } as unknown as ProjectListItem);

  return Array.from({ length: count }, (_, index) => {
    const id = -1 * ((startRow ?? 0) + index + 1);

    return failedRow(id);
  });
};
